import React, { useContext, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

import ContextComponent from "../../../AppContext";
import Utils from "../../../Utils";

const COLORS = {
   TOP_1: "#66CDAA",
  TOP_3: "#5F9EA0",
  DEFAULT: "#4682B4",
};

const getBarColor = (index) => {
  if (index === 0) return COLORS.TOP_1;
  if (index < 3) return COLORS.TOP_3;
  return COLORS.DEFAULT;
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="submission-ranking-tooltip">
      <div className="submission-ranking-tooltip-title">
        {payload[0].payload.name}
      </div>

      <div className="submission-ranking-tooltip-value">
        {payload[0].value.toLocaleString()} submissions
      </div>
    </div>
  );
};

const truncateLabel = (label) =>
  label.length > 30
    ? `${label.substring(0, 30)}...`
    : label;

const SubmissionRanking = () => {
  const { setLoadingState, setAlert, setCanRedirectToLogin } =
    useContext(ContextComponent);
  const [fromDate, setFromDate] = useState("2023-01-01");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [chartData, setChartData] = useState([]);
  const [canRender, setCanRender] = useState(false);

  useEffect(() => {
    fetchTopSubmissions(fromDate, toDate);
  }, []);

  const formatDateToISO = (date, isEndDate = false) => {
    const parsedDate = new Date(date);

    if (isEndDate) {
      parsedDate.setHours(23, 59, 59, 999);
    } else {
      parsedDate.setHours(0, 0, 0, 0);
    }

    return parsedDate.toISOString();
  };

  const fetchTopSubmissions = (from, to) => {
    const fetchSuccess = (res) => {
      setLoadingState({
        applyMask: false,
      });

      const formattedData = res?.data.map((item) => ({
        name: item?.site,
        submissions: item?.submissionCount,
      }));

      setChartData(formattedData);
      setCanRender(formattedData.length > 0);
    };

    const fetchFailure = (err) => {
      setLoadingState({
        applyMask: false,
      });
      setAlert({
        show: true,
        message: "Failed to load submission ranking. Please try again later.",
        type: "danger",
      });
      console.error("Error fetching top submissions:", err);
      setCanRender(false);
      if (err?.redirect) {
        setCanRedirectToLogin(true);
      }
    };

    setLoadingState({
      applyMask: true,
      text: "Loading submission ranking",
    });
    const formattedFromDate = formatDateToISO(from);

    const formattedToDate = formatDateToISO(to, true);
    Utils.getTopTenSubmissions(formattedFromDate, formattedToDate).then(
      fetchSuccess,
      fetchFailure,
    );
  };

  const handleApply = () => {
    if (!fromDate || !toDate || fromDate > toDate) return;

    // TODO:cPass fromDate & toDate to API endpoint

    fetchTopSubmissions(fromDate, toDate);
  };

  return (
    <div className="submission-ranking-card">
      <div className="submission-ranking-header">
        <div>
          <h6 className="submission-ranking-title">
            Top 10 assessments by submission volume — global ranking
          </h6>

          <p className="submission-ranking-subtitle">
            Ranked by number of valid submissions across all regions.
          </p>
        </div>

        <div className="submission-ranking-filter-bar">
          <div className="submission-ranking-input-group">
            <span className="submission-ranking-floating-label">From</span>

            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="submission-ranking-date-input"
            />
          </div>

          <div className="submission-ranking-input-group">
            <span className="submission-ranking-floating-label">To</span>

            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="submission-ranking-date-input"
            />
          </div>

          <button
            onClick={handleApply}
            className="submission-ranking-apply-btn"
          >
            Apply
          </button>
        </div>
      </div>

      {canRender ? (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 30, left: 40, bottom: 20 }}
          >
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tickMargin={5}
              tick={{ fontSize: 11, fill: "#304697" }}
              label={{
                value: "Number of submissions",
                position: "insideBottom",
                offset: -10,
                fontSize: 11,
                fill: "#304697",
              }}
            />

            <YAxis
              type="category"
              dataKey="name"
              width={160}
              axisLine={false}
              tickLine={false}
              tickMargin={5}
              tick={{ fontSize: 12, fill: "#304697" }}
              tickFormatter={truncateLabel}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={false}
              shared={false}
            />

            <Bar
              dataKey="submissions"
              radius={[0, 4, 4, 0]}
              barSize={22}
              cursor="pointer"
              animationDuration={1000}
            >
              {chartData.map((_, index) => (
                <Cell key={index} fill={getBarColor(index)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="submission-ranking-no-data">
          No submission data available for the selected date range.
        </div>
      )}
    </div>
  );
};

export default SubmissionRanking;
